//Libraries
import { useEffect } from 'react';
import { Button, Field } from '@effizency/efz-design-system';
import { useZodForm } from '@effizency/efz-toolkit/hooks';
import { Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

//Icons
import { ReactComponent as EfzBackground } from 'assets/@efz/backgrounds/login-background.svg';
import { ReactComponent as PlatformLogo } from 'assets/@efz/logos/logo-efz.svg';

//Components
import IntlMessages from 'components/util/IntlMessages';

//Constants
import { BASENAME_URL_LOGIN, BASENAME_URL_OPPORTUNITIES } from 'constants/settings';

//Hooks
import useRecoveryPassword from 'hooks/recoveryPassword';

//Schemas
import { resetPasswordFormSchema } from 'schemas/forms/auth';

//Services
import { getFormErrorMessage, intlMessages, isDefined } from 'services/util/auxiliaryUtils';

//Stores
import { useUserStore } from 'stores/user';

import type { TResetPasswordFormValues } from 'types/forms/auth';

//Styles
import {
    StyledLogin,
    StyledLoginContainer,
    StyledLoginContent,
    StyledLoginForm,
    StyledLoginFormFieldGroup,
    StyledLoginSVG,
} from '../Login/index.styled';

const ResetPassword = () => {
    const navigate = useNavigate();
    const {
        user_type_id,
        auth: { isUserAuthenticated },
    } = useUserStore((state) => state.user);
    const { fetchResetPassword, isRequesting, msgResetPassword } = useRecoveryPassword();

    const {
        handleSubmit,
        control,
        formState: { errors },
    } = useZodForm({
        schema: resetPasswordFormSchema,
        defaultValues: {
            email: '',
        },
    });

    // componentWillUnmountWithUseEffect
    useEffect(() => {
        return () => {
            toast.dismiss(); // dismiss all notifications
        };
    }, []); //eslint-disable-line

    // if user is authenticated, redirect
    useEffect(() => {
        if (isUserAuthenticated && isDefined(user_type_id)) {
            navigate(BASENAME_URL_OPPORTUNITIES);
        }
    }, [isUserAuthenticated]); //eslint-disable-line

    const handleOnSubmit = ({ email }: TResetPasswordFormValues) => {
        fetchResetPassword(email.toLowerCase());
    };

    return (
        <StyledLogin>
            <StyledLoginSVG>
                <div className="login-svg-ng">
                    <EfzBackground preserveAspectRatio="xMidYMid slice" />
                </div>
            </StyledLoginSVG>

            <StyledLoginContainer>
                <StyledLoginContent>
                    {/* Header */}
                    <div className="login-card-header">
                        <PlatformLogo />
                    </div>
                    {/* Body */}
                    <div className="login-card-body">
                        <h1>
                            <IntlMessages id={'page.resetPassword.subtitle'} />
                        </h1>

                        <StyledLoginForm autoComplete={'false'} onSubmit={handleSubmit(handleOnSubmit)}>
                            {/* email */}
                            <StyledLoginFormFieldGroup>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field: { ref: _ref, ...field } }) => (
                                        <Field
                                            {...field}
                                            value={field.value ?? ''}
                                            size="md"
                                            maxWidth="full"
                                            disabled={isRequesting}
                                            error={!!errors?.email}
                                            errorMessage={getFormErrorMessage(errors?.email)}
                                            placeholder={intlMessages('placeholder.emailExample')}
                                            dataTestId="reset-password-email"
                                        />
                                    )}
                                />
                                {!!msgResetPassword && (
                                    <p>
                                        <IntlMessages id={msgResetPassword} />
                                    </p>
                                )}
                            </StyledLoginFormFieldGroup>

                            {/*actions*/}
                            <Button
                                variant="primary"
                                size="bg"
                                type="submit"
                                disabled={isRequesting}
                                loading={isRequesting}
                                dataTestId="reset-password"
                            >
                                <IntlMessages id={'label.resetPassword'} />
                            </Button>

                            {/*Go to Login*/}
                            <Button
                                variant="tertiary"
                                size="sm"
                                disabled={isRequesting}
                                onClick={() => navigate(BASENAME_URL_LOGIN)}
                                dataTestId="reset-password-back-login"
                            >
                                <IntlMessages id="label.backLogin" />
                            </Button>
                        </StyledLoginForm>
                    </div>
                </StyledLoginContent>
            </StyledLoginContainer>
        </StyledLogin>
    );
};

export default ResetPassword;
